import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { ConversationMinimizedResponseDto } from '../../../../../store/rtk-query/apis/outreach/dto/response/conversation-minimized.response';
import { useLazyGetConversationsByGroupQuery } from '../../../../../store/rtk-query/apis/outreach/outreach.toolkit-api';
import { ConversationGroupResponseDto } from '../../../../../store/rtk-query/apis/outreach/dto/response/conversations-groups-response.dto';
import { AppRouting } from '../../../../../services/appRoutingResolver';
import { usePageFilters } from '../../../../../hooks/use-page-filters.hooks';
import { ConversationGroupsEnum } from '../../../../../enums/outreach/conversation-groups.enum';

type UseConversationGroupsInfiniteQueryProps = {
  conversationGroups: ConversationGroupResponseDto[];
  pageSize?: number;
  positionId: string;
};

type GroupState = {
  conversations: ConversationMinimizedResponseDto[];
  cursor?: string;
  hasNext: boolean;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
};

type GroupedConversationsState = {
  [groupName: string]: GroupState;
};

const DEFAULT_PAGE_SIZE = 25;

export type UseConversationGroupsInfiniteQueryReturn = {
  groupedConversations: GroupedConversationsState;
  loadMore: (groupName: ConversationGroupsEnum, isFirstLoad?: boolean) => Promise<void>;
  resetGroup: (groupName: ConversationGroupsEnum) => void;
  isError: boolean;
  isConversationsEmpty: boolean;
};

const filterConversations = (conversations: ConversationMinimizedResponseDto[], search: string): ConversationMinimizedResponseDto[] => {
  if (!search) return conversations;
  const lowerSearch = search.toLowerCase();

  return conversations.filter((convo) => {
    const firstName = convo.match?.talent?.firstName || '';
    const lastName = convo.match?.talent?.lastName || '';
    
    return `${firstName} ${lastName}`.toLowerCase().includes(lowerSearch);
  });
};

export function useConversationGroupsInfiniteQuery({
  positionId,
  conversationGroups,
  pageSize = DEFAULT_PAGE_SIZE,
}: UseConversationGroupsInfiniteQueryProps): UseConversationGroupsInfiniteQueryReturn {
  const {
    getFiltersByKey,
  } = usePageFilters();
  const search = getFiltersByKey(AppRouting.URL_PARAMS.OUTREACH.SEARCH)[0];

  const [fetchConversations, { isError: isFetchConversationsError }] = useLazyGetConversationsByGroupQuery();
  const [groupedConversations, setGroupedConversations] = useState<GroupedConversationsState | null>(null);
  const [isConversationsEmpty, setIsConversationsEmpty] = useState<boolean>(false);

  useEffect(() => {
    const initial: GroupedConversationsState = {};
    conversationGroups.forEach(({ groupName, count }) => {
      initial[groupName] = {
        conversations: [],
        hasNext: !!count,
        isLoading: false,
        isFetching: false,
        isError: false,
      };
    });

    if (conversationGroups.length) {
      setIsConversationsEmpty(conversationGroups.every(({ count }) => count === 0));
    }
    
    setGroupedConversations(initial);
  }, [conversationGroups]);
  
  const loadMore = useCallback(
    async (groupName: ConversationGroupsEnum, isFirstLoad = false) => {
      if (!groupedConversations) return;
      
      const group = groupedConversations[groupName];

      if (!group || group.isLoading || group.isFetching || !group.hasNext || group.isError) return;

      setGroupedConversations((prev) => ({
        ...prev,
        [groupName]: {
          ...group,
          isLoading: isFirstLoad,
          isFetching: !isFirstLoad,
          isError: false,
        },
      }));

      try {
        const response = await fetchConversations({
          positionId,
          groupName,
          cursor: group.cursor,
          limit: pageSize,
        }).unwrap();

        setGroupedConversations((prev) => {
          const newConversations = [
            ...(prev[groupName]?.conversations || []),
            ...response.results,
          ];

          return {
            ...prev,
            [groupName]: {
              conversations: newConversations,
              cursor: response.cursor,
              hasNext: response.hasNext,
              isLoading: false,
              isFetching: false,
              isError: false,
            },
          };
        });
      } catch (error) {
        setGroupedConversations((prev) => ({
          ...prev,
          [groupName]: {
            ...group,
            isLoading: false,
            isFetching: false,
            isError: true,
          },
        }));
      }
    },
    [fetchConversations, groupedConversations, pageSize, positionId]);

  const resetGroup = useCallback((groupName: ConversationGroupsEnum) => {
    setGroupedConversations((prev) => ({
      ...prev,
      [groupName]: {
        conversations: [],
        hasNext: true,
        isLoading: false,
        isFetching: false,
        isError: false,
      },
    }));
  }, []);

  useEffect(() => {
    if (!groupedConversations) return;
    
    Object.entries(groupedConversations).forEach(([groupName, groupData]) => {
      if (groupData.hasNext && groupData.conversations.length === 0 && !groupData.isLoading) {
        loadMore(groupName as ConversationGroupsEnum, true);
      }
    });
  }, [groupedConversations, loadMore]);

  const filteredGroupedConversations = useMemo(() => {
    if (!groupedConversations) return {};
    
    return Object.fromEntries(
      Object.entries(groupedConversations).map(([groupName, groupData]) => [
        groupName,
        {
          ...groupData,
          conversations: filterConversations(groupData.conversations, search),
        },
      ]),
    );
  }, [groupedConversations, search]);
  
  return {
    isError: isFetchConversationsError,
    groupedConversations: filteredGroupedConversations,
    loadMore,
    resetGroup,
    isConversationsEmpty,
  };
}
